import { AxiosError } from 'axios'
import { defaultRequestInterceptors, defaultResponseInterceptors } from './config'

import { AxiosInstance, InternalAxiosRequestConfig, RequestConfig, AxiosResponse } from './types'
import { ElMessage } from 'element-plus'
import { REQUEST_TIMEOUT } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'
import axios from 'axios'

const userStore = useUserStoreWithOut()

export const PATH_URL = import.meta.env.VITE_API_BASE_PATH
const HAS_API_PREFIX_IN_BASE_URL =
  typeof PATH_URL === 'string' && /\/api\/?$/.test(PATH_URL.trim())

const abortControllerMap: Map<string, AbortController> = new Map()

const axiosInstance: AxiosInstance = axios.create({
  timeout: REQUEST_TIMEOUT,
  baseURL: PATH_URL
})

axiosInstance.interceptors.request.use((res: InternalAxiosRequestConfig) => {
  // Normalize duplicate API prefix:
  // baseURL is '/api', while some APIs still declare url as '/api/**'.
  // Without normalization this becomes '/api/api/**' and backend returns errors.
  if (
    HAS_API_PREFIX_IN_BASE_URL &&
    typeof res.url === 'string' &&
    res.url.startsWith('/api/')
  ) {
    res.url = res.url.replace(/^\/api/, '')
  }
  const controller = new AbortController()
  const url = res.url || ''
  res.signal = controller.signal
  abortControllerMap.set(
    import.meta.env.VITE_USE_MOCK === 'true' ? url.replace('/mock', '') : url,
    controller
  )
  return res
})

axiosInstance.interceptors.response.use(
  (res: AxiosResponse) => {
    const url = res.config.url || ''
    abortControllerMap.delete(url)
    // 这里不能做任何处理，否则后面的 interceptors 拿不到完整的上下文了
    return res
  },
  (error: AxiosError) => {
    console.log('err： ' + error) // for debug

    // 仅对 401 做强制登出跳转。403 常为 CORS、权限不足或与登录无关，误杀会导致「登录成功又回登录页」
    if (error.response?.status === 401) {
      // 延迟执行，确保用户能看到错误信息
      setTimeout(() => {
        // 本地登录失效后清理状态；已在登录页则禁止整页硬跳，避免匿名请求 401 刷新死循环
        const hash = window.location.hash || ''
        const path = window.location.pathname || ''
        const onLogin = /(^|#)\/login(\?|$|\/)/.test(hash) || path.includes('/login')
        import('@/store/modules/user')
          .then(({ useUserStoreWithOut }) => {
            const userStore = useUserStoreWithOut()
            userStore.logout()
          })
          .catch(() => {
            localStorage.removeItem('userInfo')
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
            sessionStorage.clear()
          })
          .finally(() => {
            if (onLogin) {
              return
            }
            const redirect = encodeURIComponent(hash || path || '/')
            window.location.href = `/#/login?redirect=${redirect}`
          })
      }, 1500)
    }

    // ElMessage.error(error.message)
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.request.use(defaultRequestInterceptors)
axiosInstance.interceptors.response.use(defaultResponseInterceptors)

const service = {
  request: (config: RequestConfig) => {
    return new Promise((resolve, reject) => {
      if (config.interceptors?.requestInterceptors) {
        config = config.interceptors.requestInterceptors(config as any)
      }

      axiosInstance
        .request(config)
        .then((res) => {
          resolve(res)
        })
        .catch((err: any) => {
          reject(err)
        })
    })
  },
  cancelRequest: (url: string | string[]) => {
    const urlList = Array.isArray(url) ? url : [url]
    for (const _url of urlList) {
      abortControllerMap.get(_url)?.abort()
      abortControllerMap.delete(_url)
    }
  },
  cancelAllRequest() {
    for (const [_, controller] of abortControllerMap) {
      controller.abort()
    }
    abortControllerMap.clear()
  }
}

export default service
